import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PackageComparison = () => {
  const [selectedCategory, setSelectedCategory] = useState('events');

  const categories = [
    { id: 'events', name: 'Events & Weddings', icon: 'Heart' },
    { id: 'realestate', name: 'Real Estate', icon: 'Building' },
    { id: 'marketing', name: 'Marketing', icon: 'Megaphone' },
    { id: 'design', name: 'Design Fiction', icon: 'Zap' }
  ];

  const packages = {
    events: [
      {
        name: "Essential Romance",
        price: "$2,500",
        duration: "6 hours",
        deliverables: "3-5 min highlight reel",
        features: [
          "Single camera coverage",
          "Basic audio recording",
          "Color correction",
          "Music licensing included",
          "Online gallery delivery"
        ],
        popular: false
      },
      {
        name: "Cinematic Love Story",
        price: "$4,500",
        duration: "8 hours",
        deliverables: "5-8 min film + ceremony",
        features: [
          "Dual camera coverage",
          "Professional audio setup",
          "Advanced color grading",
          "Custom music selection",
          "Drone footage (weather permitting)",
          "Same-day preview reel"
        ],
        popular: true
      },
      {
        name: "Complete Collection",
        price: "$7,500",
        duration: "Full day",
        deliverables: "Multiple films + raw footage",
        features: [
          "Multi-camera coverage",
          "Full ceremony & reception",
          "Professional lighting",
          "Individual speeches captured",
          "Drone cinematography",
          "Engagement session included",
          "Premium packaging"
        ],
        popular: false
      }
    ],
    realestate: [
      {
        name: "Property Showcase",
        price: "$800",
        duration: "2-3 hours",
        deliverables: "2-3 min property tour",
        features: [
          "Interior & exterior shots",
          "Smooth gimbal movements",
          "Professional lighting",
          "Background music",
          "Quick 48-hour delivery"
        ],
        popular: false
      },
      {
        name: "Premium Listing",
        price: "$1,500",
        duration: "Half day",
        deliverables: "3-5 min cinematic tour",
        features: [
          "4K resolution filming",
          "Drone aerial footage",
          "Lifestyle staging shots",
          "Custom graphics package",
          "Multiple format delivery",
          "Social media cuts"
        ],
        popular: true
      },
      {
        name: "Luxury Portfolio",
        price: "$3,000",
        duration: "Full day",
        deliverables: "Complete marketing suite",
        features: [
          "Cinematic storytelling approach",
          "Professional talent inclusion",
          "Advanced post-production",
          "Virtual tour integration",
          "Print marketing materials",
          "Ongoing campaign support"
        ],
        popular: false
      }
    ],
    marketing: [
      {
        name: "Brand Essentials",
        price: "$3,500",
        duration: "1-2 days",
        deliverables: "3-5 brand videos",
        features: [
          "Concept development",
          "Single location shoot",
          "Professional talent",
          "Basic motion graphics",
          "Platform optimization"
        ],
        popular: false
      },
      {
        name: "Campaign Creator",
        price: "$8,500",
        duration: "3-5 days",
        deliverables: "Complete campaign suite",
        features: [
          "Multi-video campaign",
          "Advanced motion graphics",
          "Multiple locations",
          "Professional casting",
          "A/B testing versions",
          "Performance analytics setup"
        ],
        popular: true
      },
      {
        name: "Brand Universe",
        price: "$15,000",
        duration: "1-2 weeks",
        deliverables: "Comprehensive content library",
        features: [
          "Brand documentary approach",
          "Multiple campaign angles",
          "Custom animation package",
          "Ongoing content creation",
          "Strategic consultation",
          "Quarterly content updates"
        ],
        popular: false
      }
    ],
    design: [
      {
        name: "Concept Visualization",
        price: "$5,000",
        duration: "2-3 weeks",
        deliverables: "3-5 min concept film",
        features: [
          "Storyboard development",
          "3D visualization",
          "Experimental techniques",
          "Sound design",
          "Future-focused narrative"
        ],
        popular: false
      },
      {
        name: "Innovation Showcase",
        price: "$12,000",
        duration: "4-6 weeks",
        deliverables: "Complete vision presentation",
        features: [
          "Advanced CGI integration",
          "Interactive elements",
          "Multi-platform delivery",
          "Investor presentation format",
          "Technical documentation",
          "Prototype visualization"
        ],
        popular: true
      },
      {
        name: "Future Vision",
        price: "$25,000",
        duration: "8-12 weeks",
        deliverables: "Immersive experience suite",
        features: [
          "VR/AR compatibility",
          "Custom software development",
          "Interactive installations",
          "Multi-sensory design",
          "Exhibition-ready format",
          "Ongoing technical support"
        ],
        popular: false
      }
    ]
  };

  const currentPackages = packages?.[selectedCategory];

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Package" size={16} />
            <span>Transparent Pricing</span>
          </div>
          
          <h2 className="text-fluid-4xl font-display font-bold text-primary mb-6">
            Packages Tailored to
            <span className="text-accent"> Your Vision</span>
          </h2>
          
          <p className="text-fluid-lg text-muted-foreground leading-relaxed">
            Choose the perfect package for your project. All packages include our signature quality and dedicated support.
          </p>
        </div>

        {/* Category Selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                selectedCategory === category?.id
                  ? 'bg-accent text-accent-foreground shadow-lg'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={category?.icon} size={18} />
              <span>{category?.name}</span>
            </button>
          ))}
        </div>

        {/* Package Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {currentPackages?.map((pkg, index) => (
            <div
              key={index}
              className={`relative bg-card border rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                pkg?.popular 
                  ? 'border-accent shadow-lg ring-2 ring-accent/20' 
                  : 'border-border hover:border-accent/50'
              }`}
            >
              {pkg?.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-accent text-accent-foreground px-4 py-2 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-primary mb-2 font-display">
                  {pkg?.name}
                </h3>
                <div className="text-3xl font-bold text-accent mb-2">
                  {pkg?.price}
                </div>
                <p className="text-sm text-muted-foreground mb-1">
                  {pkg?.duration}
                </p>
                <p className="text-sm font-medium text-foreground">
                  {pkg?.deliverables}
                </p>
              </div>

              <div className="space-y-4 mb-8">
                {pkg?.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <Icon name="Check" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <Button
                  variant={pkg?.popular ? "default" : "outline"}
                  fullWidth
                  className={pkg?.popular ? "bg-accent hover:bg-accent/90 text-accent-foreground" : ""}
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  Get Started
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  size="sm"
                  iconName="Info"
                  iconPosition="left"
                >
                  Learn More
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Package CTA */}
        <div className="text-center mt-16">
          <div className="bg-muted/50 border border-border rounded-2xl p-8 max-w-2xl mx-auto">
            <Icon name="Sparkles" size={32} className="text-accent mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-primary mb-4 font-display">
              Need Something Different?
            </h3>
            <p className="text-muted-foreground mb-6">
              Every project is unique. Let's create a custom package that perfectly fits your vision and budget.
            </p>
            <Button
              variant="default"
              className="bg-cta hover:bg-cta/90 text-cta-foreground"
              iconName="Zap"
              iconPosition="left"
            >
              Create Custom Package
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PackageComparison;